import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Alert, 
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { 
  Search as SearchIcon, 
  Clear as ClearIcon, 
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { getNews, updateNewsStatus, deleteNews, News } from '../services/newsService';
import AddNewsDialog from '../components/news/AddNewsDialog';

interface NewsRow extends News {}

export const NewsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<NewsRow[]>([]);
  const [filteredRows, setFilteredRows] = useState<NewsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'id', sort: 'desc' },
  ]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newsToDelete, setNewsToDelete] = useState<News | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const fetchNews = async () => {
    try {
      setLoading(true);
      const data = await getNews();
      setRows(data);
      setFilteredRows(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Error al cargar las novedades. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  // Filter rows based on search text
  useEffect(() => {
    if (searchText === '') {
      setFilteredRows(rows);
    } else {
      const filtered = rows.filter(row => 
        Object.values(row).some(
          value => 
            value && 
            value.toString().toLowerCase().includes(searchText.toLowerCase())
        )
      );
      setFilteredRows(filtered);
    }
  }, [searchText, rows]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchText('');
  };

  const handleAddClick = () => {
    setAddDialogOpen(true);
  };

  const handleAddDialogClose = (created = false) => {
    setAddDialogOpen(false);
    if (created) {
      fetchNews();
    }
  };

  const handleNewsCreated = (news: News) => {
    setRows(prevRows => [news, ...prevRows]);
  };

  const handleDeleteClick = (news: News) => {
    setNewsToDelete(news);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!newsToDelete) return;
    
    try {
      setIsDeleting(true);
      const success = await deleteNews(newsToDelete.id);
      
      if (success) {
        setRows(prevRows => prevRows.filter(row => row.id !== newsToDelete.id));
        enqueueSnackbar('Novedad eliminada correctamente', { variant: 'success' });
      }
    } catch (error: any) {
      console.error('Error deleting news:', error);
      enqueueSnackbar(error.message || 'Error al eliminar la novedad', { 
        variant: 'error' 
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setNewsToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setNewsToDelete(null);
  };

  const handleStatusToggle = async (id: number, currentStatus: 'activo' | 'inactivo') => {
    try {
      const newStatus = currentStatus === 'activo' ? 'inactivo' : 'activo';
      const updatedNews = await updateNewsStatus(id, newStatus);
      
      setRows(rows.map(row => 
        row.id === id ? { ...row, status: updatedNews.status } : row
      ));
      
      enqueueSnackbar('Estado actualizado correctamente', { variant: 'success' });
    } catch (err) {
      console.error('Error updating news status:', err);
      enqueueSnackbar('Error al actualizar el estado', { variant: 'error' });
    }
  };

  // Data URL for a transparent 1x1 pixel image
  const transparentPixel = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

  const columns: GridColDef<NewsRow>[] = [
    { 
      field: 'img', 
      headerName: 'Imagen', 
      width: 120,
      sortable: false,
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box
          component="img"
          src={params.value || transparentPixel}
          alt="Novedad"
          sx={{
            width: 50,
            height: 50,
            objectFit: 'cover',
            borderRadius: 1,
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            color: '#666',
            textAlign: 'center',
            padding: '4px',
            boxSizing: 'border-box',
            '&::after': {
              content: '"Sin imagen"',
              display: !params.value ? 'block' : 'none',
            }
          }}
        />
      )
    },
    { 
      field: 'title', 
      headerName: 'Título', 
      flex: 1,
      minWidth: 200,
      headerAlign: 'left',
      align: 'left',
      cellClassName: 'left-aligned-cell',
    },
    { 
      field: 'description', 
      headerName: 'Descripción', 
      flex: 1,
      minWidth: 300,
      headerAlign: 'left',
      align: 'left',
      cellClassName: 'left-aligned-cell',
    },
    {
      field: 'status',
      headerName: 'ESTADO',
      width: 150,
      headerAlign: 'left',
      align: 'left',
      cellClassName: 'left-aligned-cell',
      renderCell: (params) => {
        const isActive = params.row.status === 'activo';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={isActive}
                onChange={() => handleStatusToggle(params.row.id, params.row.status)}
                color={isActive ? 'success' : 'default'}
                size="small"
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: isActive ? 'success.main' : 'text.secondary',
                  fontWeight: 500,
                  minWidth: 70,
                  textAlign: 'center'
                }}
              >
                {isActive ? 'Activo' : 'Inactivo'}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      headerAlign: 'left',
      align: 'left',
      cellClassName: 'left-aligned-cell',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" justifyContent="center" width="100%">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(params.row);
            }}
            disabled={isDeleting}
            sx={{
              color: 'rgba(0, 0, 0, 0.7)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                color: 'rgba(0, 0, 0, 0.9)'
              },
              '&.Mui-disabled': {
                color: 'rgba(0, 0, 0, 0.26)',
              }
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirmar eliminación
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Está seguro que desea eliminar la novedad "{newsToDelete?.title}"? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteCancel} 
            variant="outlined"
            disabled={isDeleting}
            sx={{
              color: '#757575',
              borderColor: '#BDBDBD',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderColor: '#9E9E9E',
                color: '#424242',
              },
              textTransform: 'none',
              fontWeight: 500,
              minWidth: '100px',
              height: '36px',
              borderRadius: '4px',
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="outlined"
            disabled={isDeleting}
            autoFocus
            sx={{
              backgroundColor: 'white',
              color: '#C8102E',
              borderColor: '#C8102E',
              '&:hover': {
                backgroundColor: 'rgba(200, 16, 46, 0.04)',
                borderColor: '#A50E30',
                color: '#A50E30',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                color: 'rgba(0, 0, 0, 0.26)',
                borderColor: 'rgba(0, 0, 0, 0.12)',
              },
              textTransform: 'none',
              fontWeight: 500,
              minWidth: '100px',
              height: '36px',
              borderRadius: '4px',
              ml: 1,
            }}
          >
            {isDeleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, bgcolor: 'background.paper', p: 3, borderRadius: 2, boxShadow: 1 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Novedades para clientes
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          sx={{
            color: '#C8102E',
            backgroundColor: 'white',
            borderColor: '#C8102E',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#C8102E',
              color: 'white',
              borderColor: '#C8102E',
            },
            textTransform: 'none',
            minWidth: '180px',
            height: '40px',
            borderRadius: '4px',
          }}
        >
          Nueva Novedad
        </Button>
      </Box>

      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar novedades..."
          value={searchText}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchText && (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} edge="end">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ 
        height: '70vh',
        width: '100%',
        '& .left-aligned-cell': {
          justifyContent: 'flex-start',
          paddingLeft: '24px',
        },
        '& .MuiDataGrid-root': {
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            borderRight: 'none',
            padding: '8px 16px',
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: 'none',
            padding: '8px 16px',
            justifyContent: 'center',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
            width: '100%',
            textAlign: 'center',
          },
          '& .MuiDataGrid-row': {
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell--textLeft, & .MuiDataGrid-cell--textRight': {
            justifyContent: 'center',
          },
        },
      }}>
        <DataGrid
          rows={filteredRows}
          columns={columns.map(column => ({
            ...column,
            headerAlign: 'center',
            align: 'center',
          }))}
          loading={loading}
          pageSizeOptions={[5, 10, 25]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          disableColumnMenu={false}
          disableColumnFilter={false}
          disableColumnSelector={false}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
          }}
        />
      </Box>
      
      <AddNewsDialog 
        open={addDialogOpen} 
        onClose={handleAddDialogClose}
        onNewsCreated={handleNewsCreated}
      />
    </Container>
    </>
  );
};

export default NewsPage;
